import * as React from 'react'
import { Chip } from './Chip'
import styled from 'styled-components'

interface IState {
  isFocus: boolean
}

export interface IChipsInputProps extends React.InputHTMLAttributes<any> {
  label: string
  onDeleteElement: (element: string) => void
  onAddElement: (element: string) => void
  color?: string
  values?: Array<string>
  hasError?: boolean
  errorMessage?: string | null | undefined | {}
}

interface IChipsInputLabelProps {
  themeColor: string
  isFocus: boolean
  hasValue: boolean
  disabled?: boolean
  hasError?: boolean
}

const StyledChipsInput = styled.div`
  display: flex;
  position: relative;
  box-sizing: border-box;
  flex-wrap: wrap;
  width: 100%;
`

const StyledField = styled.label<{
  hasError?: boolean
  isFocus: boolean
  themeColor: string
  disabled?: boolean
}>`
  display: flex;
  flex-direction: column;
  min-height: 22px;
  border: none;
  border-bottom: 1px solid #e0e0e0;

  font-size: ${(props) => props.theme.defaultFontSize};
  font-family: ${(props) => props.theme.fontFamily};
  box-sizing: border-box;

  padding-bottom: 4px;

  border: 0;
  border-bottom: ${(props) => {
    if (props.hasError) return `1px solid ${props.theme.errorColor}`
    else if (props.isFocus) return `2px solid ${props.theme[props.themeColor]}`
    else if (props.disabled) return `1px dashed ${props.theme.greyColor}`
    else return `1px solid ${props.theme.greyColor}`
  }};

  transition: border 150ms ease-out;

  outline: 0;

  &:disabled {
    background: transparent;
    border-bottom: 1px dashed ${(props) => props.theme.greyColor};
  }

  position: relative;
`

const StyledChipsInputLabel = styled.label<IChipsInputLabelProps>`
  font-size: ${(props) => props.theme.defaultFontSize};
  font-family: ${(props) => props.theme.fontFamily};

  color: ${(props) => {
    if (props.disabled) return props.theme.disabledColor
    else if (props.hasError) return props.theme.errorColor
    else if (props.isFocus) return props.theme[props.themeColor]
    else return props.theme.greyTextColor
  }};

  position: absolute;
  left: 0;
  transform: ${(props) => (props.isFocus || props.hasValue ? 'translate3d(0, -100%, 0);font-size: 85%;' : '')};

  pointer-events: none;

  transition: font-size 200ms, transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
`

const StyledChipsInputField = styled.input`
  border: none;
  outline: none;
  background-color: transparent;
  flex-grow: 1;
`

const ErrorLabel = styled.label`
  font-size: calc(${(props) => props.theme.defaultFontSize} * 0.85);
  font-family: ${(props) => props.theme.fontFamily};
  color: ${(props) => props.theme.errorColor};
  position: absolute;
  bottom: -20px;
`

/**
 * ChipsInput
 */
/** @component */
export class ChipsInput extends React.Component<IChipsInputProps, IState> {
  constructor(props: IChipsInputProps) {
    super(props)
    this.state = {
      isFocus: false,
    }
  }

  focus = () => {
    this.setState({ isFocus: true })
  }

  blur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target
    this.props.onAddElement(value)
    this.setState({ isFocus: false })
    ;(e.target as HTMLInputElement).value = ''
  }

  onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement
    const { value } = target
    const { key } = e
    if (key == 'Enter') {
      if (value.trim() == '') {
        target.blur()
        this.setState({ isFocus: false })
      } else {
        this.props.onAddElement(value)
      }
      target.value = ''
    } else if (key == 'Backspace' && value == '' && this.props.values) {
      this.props.onDeleteElement(this.props.values[this.props.values.length - 1])
    }
  }

  render() {
    const { color, label, id, disabled, hasError, errorMessage, values } = this.props

    const { isFocus } = this.state

    const hasValue = Boolean(this.props.values && this.props.values.length > 0)
    const ErrorDOM = hasError ? <div>{errorMessage}</div> : null
    return (
      <>
        <StyledField isFocus={isFocus} themeColor={color!} disabled={disabled} htmlFor={id} {...this.props}>
          <StyledChipsInputLabel
            htmlFor={id}
            isFocus={isFocus}
            hasValue={hasValue}
            themeColor={color!}
            disabled={disabled}
          >
            {label}
          </StyledChipsInputLabel>
          <StyledChipsInput>
            {values &&
              values.map((a, i) => (
                <Chip key={i} deleteFunction={this.props.onDeleteElement} label={a} disabled={disabled} />
              ))}
            <StyledChipsInputField
              id={id}
              onFocus={this.focus}
              onBlur={this.blur}
              onKeyDown={this.onKeyPress}
              disabled={disabled}
              {...this.props}
            />
          </StyledChipsInput>
          <ErrorLabel>{ErrorDOM}</ErrorLabel>
        </StyledField>
      </>
    )
  }

  static defaultProps = {
    color: 'secondaryColor',
  }
}
